/** Where a record was opened from (EXP-12, ADR-0212).
 *
 * A record page is reached from many lists - the Scope tables, All emissions, Documents, Processing,
 * Action items, a filing - and the way back from it has to be **the list the reader was on, with the
 * filters and the window it had**, not a fixed destination. The page used to hard-code its own
 * scope's table, so somebody who had narrowed All emissions to one site and opened the fortieth row
 * was returned to a different screen, and nothing failed.
 *
 * The router knows the location it navigated from; nothing keeps it. This keeps exactly one thing:
 * **the last place that was not a record**, set when a record is entered from it. Moving from one
 * record to another (a revision, a related record) does not replace it, because the list is still
 * the list the reader started from. A page opened directly (a pasted link, a reload) has no trail,
 * and the record page then falls back to its scope's table.
 *
 * Module state, deliberately not storage: it belongs to this tab's navigation, dies with the page,
 * and holds a path and a search - no figure and no person.
 */


export interface TrailPlace {
  pathname: string;
  /** The parsed search the list had when it was left, filters and window included. */
  search: Record<string, unknown>;
}

let origin: TrailPlace | undefined;

const isRecord = (pathname: string) => pathname.startsWith("/records/");

/** Note one navigation. Called by the router's `onResolved` listener (`followRouter`); exported so a
 * test can drive it without constructing a router. */
export function noteNavigation(from: TrailPlace | undefined, to: TrailPlace): void {
  if (!isRecord(to.pathname)) return;
  if (!from || isRecord(from.pathname)) return;
  origin = { pathname: from.pathname, search: { ...from.search } };
}

/** The list the current record was opened from, if there was one. */
export function recordOrigin(): TrailPlace | undefined {
  return origin;
}

/** Forget it (tests, and sign-out would not need it: the trail holds no data). */
export function resetTrail(): void {
  origin = undefined;
}

/** The structural slice of the router this needs, so a test can pass a stand-in. */
interface RouterLike {
  subscribe: (
    event: "onResolved",
    listener: (info: {
      fromLocation?: { pathname: string; search: unknown };
      toLocation: { pathname: string; search: unknown };
    }) => void,
  ) => () => void;
}

/** Keep the trail as the router moves. Returns the unsubscribe. */
export function followRouter(router: RouterLike): () => void {
  return router.subscribe("onResolved", ({ fromLocation, toLocation }) => {
    noteNavigation(
      fromLocation ? { pathname: fromLocation.pathname, search: fromLocation.search as Record<string, unknown> } : undefined,
      { pathname: toLocation.pathname, search: toLocation.search as Record<string, unknown> },
    );
  });
}
